import axios from 'axios';
import authService from './authService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

/**
 * Subscription Service
 * Handles all subscription-related API calls
 */
const subscriptionService = {
    /**
     * Get user's subscriptions
     * @returns {Promise} API response with subscriptions data
     */
    getUserSubscriptions: async () => {
        try {
            const token = authService.getToken();
            
            if (!token) {
                return {
                    success: false,
                    error: 'NO_TOKEN',
                    message: 'No authentication token found'
                };
            }

            const response = await axios.get(`${API_URL}/api/v1/subscriptions`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return {
                success: true,
                data: response.data.data || []
            };
        } catch (error) {
            // Handle error responses from API
            if (error.response) {
                const errorData = error.response.data;
                const errorCode = errorData.error?.code;

                // Handle token-related errors
                if (errorCode === 'TOKEN_MISSING' || 
                    errorCode === 'TOKEN_INVALID' || 
                    errorCode === 'TOKEN_EXPIRED') {
                    return {
                        success: false,
                        error: errorCode,
                        message: errorData.error?.message || errorData.message,
                        requiresLogout: true
                    };
                }

                return {
                    success: false,
                    error: errorCode || 'API_ERROR',
                    message: errorData.message || 'Failed to fetch subscriptions',
                    status: error.response.status
                };
            }

            // Handle network errors
            return {
                success: false,
                error: 'NETWORK_ERROR',
                message: 'Network error. Please check your connection.',
                status: null
            };
        }
    },

    /**
     * Format subscription status for display
     * @param {string} status - Subscription status
     * @returns {object} Display text and color class
     */
    formatStatus: (status) => {
        const statusMap = {
            'active': { text: 'Active', color: 'text-emerald-400', bgColor: 'bg-emerald-500/10', borderColor: 'border-emerald-500/30' },
            'trialing': { text: 'Trial', color: 'text-blue-400', bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/30' },
            'past_due': { text: 'Payment Failed', color: 'text-orange-400', bgColor: 'bg-orange-500/10', borderColor: 'border-orange-500/30' },
            'canceled': { text: 'Canceled', color: 'text-slate-400', bgColor: 'bg-slate-500/10', borderColor: 'border-slate-500/30' }
        };

        return statusMap[status] || { text: status, color: 'text-slate-400', bgColor: 'bg-slate-500/10', borderColor: 'border-slate-500/30' };
    },

    /**
     * Format date for display
     * @param {string} dateString - ISO date string
     * @returns {string} Formatted date
     */
    formatDate: (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    },

    /**
     * Get all available subscription plans
     * @returns {Promise} API response with all subscription plans
     */
    getAllPlans: async () => {
        try {
            const token = authService.getToken();
            
            const headers = token ? {
                'Authorization': `Bearer ${token}`
            } : {};

            const response = await axios.get(`${API_URL}/api/v1/subscriptions/plans`, {
                headers
            });

            return {
                success: true,
                data: response.data.data || []
            };
        } catch (error) {
            // Handle error responses from API
            if (error.response) {
                const errorData = error.response.data;
                const errorCode = errorData.error?.code;

                return {
                    success: false,
                    error: errorCode || 'API_ERROR',
                    message: errorData.message || 'Failed to fetch subscription plans',
                    status: error.response.status
                };
            }

            // Handle network errors
            return {
                success: false,
                error: 'NETWORK_ERROR',
                message: 'Network error. Please check your connection.',
                status: null
            };
        }
    },

    /**
     * Create Stripe Checkout session for a subscription plan
     * @param {string} planKey - The plan key (e.g., 'NBA_PRO', 'AFL_ELITE')
     * @returns {Promise} API response with checkout session URL
     */
    createCheckoutSession: async (planKey) => {
        try {
            const token = authService.getToken();
            
            if (!token) {
                return {
                    success: false,
                    error: 'NO_TOKEN',
                    message: 'Please log in to purchase a subscription'
                };
            }

            const response = await axios.post(
                `${API_URL}/api/v1/stripe/create-checkout-session`,
                { plan_key: planKey },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            // Handle error responses from API
            if (error.response) {
                const errorData = error.response.data;
                
                return {
                    success: false,
                    error: errorData.error || 'API_ERROR',
                    message: errorData.message || 'Failed to create checkout session',
                    status: error.response.status
                };
            }

            // Handle network errors
            return {
                success: false,
                error: 'NETWORK_ERROR',
                message: 'Network error. Please check your connection.',
                status: null
            };
        }
    },

    /**
     * Preview a subscription change before confirming
     * @param {string} newPlanKey - The new plan key to preview
     * @returns {Promise<Object>} Preview data with pricing and timing information
     */
    previewSubscriptionChange: async (newPlanKey) => {
        try {
            const token = authService.getToken();
            if (!token) {
                return {
                    success: false,
                    error: 'TOKEN_MISSING',
                    message: 'Authentication required to preview subscription change'
                };
            }

            const response = await axios.post(
                `${API_URL}/api/v1/stripe/preview-subscription-change`,
                { new_plan_key: newPlanKey },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            return {
                success: true,
                data: response.data.data,
                message: response.data.message
            };
        } catch (error) {
            // Handle error responses from API
            if (error.response) {
                const errorData = error.response.data;
                
                return {
                    success: false,
                    error: errorData.error?.code || 'API_ERROR',
                    message: errorData.message || 'Failed to preview subscription change',
                    status: error.response.status
                };
            }

            // Handle network errors
            return {
                success: false,
                error: 'NETWORK_ERROR',
                message: 'Network error. Please check your connection.',
                status: null
            };
        }
    },

    /**
     * Change an existing subscription to a different tier
     * @param {string} newPlanKey - The new plan key to change to
     * @returns {Promise<Object>} Change confirmation data
     */
    changeSubscription: async (newPlanKey) => {
        try {
            const token = authService.getToken();
            if (!token) {
                return {
                    success: false,
                    error: 'TOKEN_MISSING',
                    message: 'Authentication required to change subscription'
                };
            }

            const response = await axios.post(
                `${API_URL}/api/v1/stripe/change-subscription`,
                { new_plan_key: newPlanKey },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            return {
                success: true,
                data: response.data.data,
                message: response.data.message
            };
        } catch (error) {
            // Handle error responses from API
            if (error.response) {
                const errorData = error.response.data;
                
                return {
                    success: false,
                    error: errorData.error?.code || errorData.error_code || 'API_ERROR',
                    message: errorData.message || 'Failed to change subscription',
                    status: error.response.status
                };
            }

            // Handle network errors
            return {
                success: false,
                error: 'NETWORK_ERROR',
                message: 'Network error. Please check your connection.',
                status: null
            };
        }
    },

    /**
     * Create Stripe Customer Portal session for managing subscriptions
     * @param {string|null} planKey - Optional plan key to manage a specific subscription (e.g., 'NBA_PRO', 'AFL_ELITE'). Omit to view all billing history.
     * @param {string|null} flowType - Optional flow type: 'payment_method_update', 'subscription_cancel', 'subscription_update'
     * @returns {Promise<Object>} Portal session URL and details
     */
    createPortalSession: async (planKey = null, flowType = null) => {
        try {
            const token = authService.getToken();
            if (!token) {
                return {
                    success: false,
                    error: 'TOKEN_MISSING',
                    message: 'Authentication required to access customer portal'
                };
            }

            const body = {};
            if (planKey) {
                body.plan_key = planKey;
            }
            if (flowType) {
                body.flow_type = flowType;
            }

            const response = await axios.post(
                `${API_URL}/api/v1/stripe/create-portal-session`,
                body,
                { 
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    } 
                }
            );

            return {
                success: true,
                data: response.data.data,
                message: response.data.message
            };
        } catch (error) {
            // Handle error responses from API
            if (error.response) {
                const errorData = error.response.data;
                
                return {
                    success: false,
                    error: errorData.error || 'API_ERROR',
                    message: errorData.message || 'Failed to create portal session',
                    status: error.response.status
                };
            }

            // Handle network errors
            return {
                success: false,
                error: 'NETWORK_ERROR',
                message: 'Network error. Please check your connection.',
                status: null
            };
        }
    }
};

export default subscriptionService;

